import { useState, useRef, useEffect } from 'react';
import { DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline'; 

const ShareButton = ({ icon: Icon, label }: { icon: any; label: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const currentUrl = window.location.href;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setCopied(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link: ', err);
    }
  };

  return (
    <div className="relative flex items-center" ref={popoverRef}>
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group flex h-10 w-10 items-center justify-center rounded-full border transition-all focus:outline-none ${
          isOpen 
            ? 'border-primary bg-primary/5 text-primary' 
            : 'border-slate-200 bg-white text-slate-500 hover:border-primary hover:text-primary'
        }`}
        aria-label={`Share on ${label}`}
        aria-expanded={isOpen}
      >
        <Icon className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="absolute bottom-full right-0 md:right-auto md:left-0 mb-3 w-72 sm:w-80 origin-bottom rounded-xl border border-slate-200 bg-white p-3 shadow-xl z-50 animate-in fade-in zoom-in-95 duration-200">
          <div className="mb-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
            Share Link
          </div>
          
          <div className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 p-1.5">
            <input
              type="text"
              readOnly
              value={currentUrl}
              className="w-full bg-transparent px-2 text-sm text-slate-600 outline-none truncate"
            />
            
            <button
              onClick={handleCopy}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-all ${
                copied 
                  ? 'bg-emerald-100 text-emerald-600' 
                  : 'bg-white border border-slate-200 text-slate-500 hover:border-primary hover:text-primary shadow-sm'
              }`}
              title={copied ? "Copied!" : "Copy link"}
            >
              {copied ? (
                <CheckIcon className="h-4 w-4" />
              ) : (
                <DocumentDuplicateIcon className="h-4 w-4 text-slate-400 hover:text-primary transition-colors" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareButton;